import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DownloadStatus } from "abweb/dto/generated/download/DownloadStatus";
import { DownloadStatusObject } from "abweb/dto/generated/download/DownloadStatusObject";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { getUserSessionInfoText } from "abweb/service/pfs/UserSessionInfo";
import { MethodType } from "abweb/service/rpc/MethodType";
import { PollMessageHandlers, PollResponseParsers } from "abweb/service/rpc/PollResponseParser";
import { BrowserCheck } from "abweb/service/utils/BrowserCheck";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { TSOGUtils } from "abweb/utils/json/TSOGUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
import { PromiseUtils } from "abweb/utils/PromiseUtils";
import { URLUtils } from "abweb/utils/URLUtils";
const LOG = LogUtils.getLogger(__filename);
export class PackageForSupportService {
  static getNextId() {
    return "pfs_" + PackageForSupportService.pfsId++;
  }
  progressDialogOptions() {
    return {
      title: AbWebResources.packageForSupport_title(),
      initialStatusMessage: AbWebResources.packageForSupport_preparingToDownloadPackageMessage()
    };
  }
  constructor(rpcService) {
    _defineProperty(this, "contentGenerators", new Map());
    _defineProperty(this, "paramGenerators", new Map());
    this.rpcService = rpcService;
    // Builtin content generators (can never be unregistered, so don't call addFileGenerator...)
    this.addBuiltinFileContentGenerator("profileStats.csv", ProfileUtils.profileStatsToCSV.bind(ProfileUtils));
    // CAT-1727: add all client logs to pfs (formerly limited to 100k due to CAT-1267)
    this.addBuiltinFileContentGenerator("logMessages.txt", LogUtils.getLoggerText.bind(LogUtils));
    this.addBuiltinFileContentGenerator("browserInfo.txt", BrowserCheck.getBrowserText.bind(BrowserCheck));
    this.addBuiltinFileContentGenerator("userSession.txt", getUserSessionInfoText);
  }
  addBuiltinFileContentGenerator(filename, fileGenFn) {
    this.contentGenerators.set(PackageForSupportService.getNextId(), () => new Map([[filename, fileGenFn()]]));
  }

  /**
   * @inheritDoc
   */
  addFileGenerator(fileGeneratorFn) {
    const nextId = PackageForSupportService.getNextId();
    this.contentGenerators.set(nextId, fileGeneratorFn);
    return () => {
      this.contentGenerators.delete(nextId);
    };
  }

  /**
   * @inheritDoc
   */
  addParameterGenerator(paramGeneratorFn) {
    const nextId = PackageForSupportService.getNextId();
    this.paramGenerators.set(nextId, paramGeneratorFn);
    return () => {
      this.paramGenerators.delete(nextId);
    };
  }

  /**
   * @inheritDoc
   */
  async createPackageForSupport(statusCallback) {
    // issue innocuous RPC in order to detect whether the session has been timed out. If it was,
    // the RPC will trigger login dialog and after "await" the session will be logged in again.
    // This is required because the subsequent rpcService.invokeDownload call isn't intercepted
    // by our just-in-time-log-in mechanism while rpcService.invokeRetJSON is.
    try {
      await this.rpcService.invokeRetJSON("/api/abweb/locale/supportedLocales", {}, MethodType.GET);
    } catch (err) {
      console.error("RPC '/api/abweb/locale/supportedLocales' caused error", err);
    }
    this.currentStatusObject = DownloadStatusObject.createDownloadStatusObject(0, DownloadStatus.INCOMPLETE);
    const pfsParams = await this.generateClientPFSParams();
    if (pfsParams == null) {
      const abortedStatus = DownloadStatusObject.createDownloadStatusObject(0, DownloadStatus.CANCELLED);
      statusCallback?.(abortedStatus);
      return abortedStatus;
    }
    const clientData = FetchUtils.fileFactory([TSOGUtils.stringify(this.generateClientPFSContent())], "clientData");
    const clientParams = FetchUtils.fileFactory([TSOGUtils.stringify(pfsParams)], "clientParams");

    // get the Zip file, via RPC, from server and stream straight to disk using browser File Download capability
    const pfsFilename = PackageForSupportService.buildPFSFilename();

    // As it uses browser File Download capability (not ajax), it is a 'void' in that the download is kicked off.
    // See CAT-1204 for discussion of being notified when file download is complete,
    // including possible implementation in linked stack overflow article.
    const reqInit = FetchUtils.buildMultiPartFormRequest();
    const invokeUrl = URLUtils.appendQueryParamsToUrl("/api/abweb/pfs/packageForSupportContent", {
      "filename": pfsFilename
    });
    return this.rpcService.invokeRetJSON(invokeUrl, {
      clientData: clientData,
      params: clientParams
    }, reqInit).then(async downloadHandle => {
      this.statusPollingTask = this.startStatusTask(downloadHandle, downloadStatusObject => {
        this.currentStatusObject = downloadStatusObject;
        statusCallback?.(downloadStatusObject);
      });
      const untilStatusPollingTaskResult = PromiseUtils.swallowCancelException2(this.statusPollingTask.result());
      this.rpcService.openDownloadLink("/api/abweb/pfs/{downloadHandle}/actions/download", {
        downloadHandle
      }, untilStatusPollingTaskResult.then());

      // If the polling task result promise returns undefined, we've been cancelled and a TaskCanceled has been swallowed
      const finalStatusObject = this.currentStatusObject = (await untilStatusPollingTaskResult) ?? DownloadStatusObject.createDownloadStatusObject(this.currentStatusObject.bytesWritten, DownloadStatus.CANCELLED);
      statusCallback?.(finalStatusObject);
      return finalStatusObject;
    });
  }

  /**
   *  @inheritDoc
   */
  cancelPackageForSupport() {
    if (this.statusPollingTask) {
      /*
       * We purposely swallow any cancel exception here to avoid bubbling error up to last chance error handler.
       */
      PromiseUtils.swallowCancelException2(this.statusPollingTask.cancel()).then();
    }
  }
  startStatusTask(downloadHandle, messageHandler) {
    return this.rpcService.invokeAsync("/api/abweb/pfs/{downloadHandle}/actions/status", {
      downloadHandle
    }, PollResponseParsers.objectParser(DownloadStatusObject), {
      streamingStatus: PollMessageHandlers.objectHandler(DownloadStatusObject, messageHandler)
    });
  }
  generateClientPFSContent() {
    const clientFiles = {};
    // Add the generated data into the package on top of the passed parameters.
    this.contentGenerators.forEach(contentGenFn => {
      try {
        const fileMap = contentGenFn();
        fileMap.forEach((fileContent, filename) => clientFiles[filename] = fileContent);
      } catch (error) {
        LOG.error(`Failed executing pfs file content generation function: ${error}`);
      }
    });
    return clientFiles;
  }
  async generateClientPFSParams() {
    const clientParams = {};
    let abort = false;

    // Add the generated data into the package on top of the passed parameters.
    await Promise.all([...this.paramGenerators.values()].map(async paramGenFn => {
      try {
        const paramGenResult = paramGenFn();
        const params = ClassUtils.isInstanceOf(paramGenResult, Promise) ? await paramGenResult : paramGenResult;
        if (params == null) {
          abort = true;
        } else {
          params.forEach((paramValue, paramName) => clientParams[paramName] = paramValue);
        }
      } catch (error) {
        LOG.error(`Failed executing pfs parameter generation function: ${error}`);
      }
    }));
    return abort ? null : clientParams;
  }
  static buildPFSFilename() {
    const productInfo = AbWebServices.sessionData.productInfo;

    // Clean the product name to only ASCII lower case letters and dashes.
    const cleanName = productInfo.name != null ? productInfo.name.replace(/\W/g, "-").toLowerCase() : "abinitio"; // no product name? return a placeholder.  Should never happen, but this IS package for support.

    return cleanName + "-pfs-" + DateTimeUtils.FORMAT_FILENAME_TIMESTAMP_SEC.format(new Date()) + ".zip";
  }
}
_defineProperty(PackageForSupportService, "pfsId", 1);
//# sourceMappingURL=PackageForSupportService.js.map
